import React, { useState, ElementType } from 'react';
import './MessageItem.css';
import { useAuth } from '../../../firebase/AuthProvider';
import { Message } from '../../../services/chatService/ChatService';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import { parseMessageContent } from '../../../utilities/chat/textUtils';
import AttachmentDisplay from './AttachmentDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion'; // For animations
import { getTimeAgo, getUTCTime } from '../../../utilities/dateUtils';
import { ValidateOtpResponseDto } from '../../../interfaces/ExtermalUserDtos/ExternalUsersDtos';
import { MessageType } from '../../../Data/SD';
import { formatSystemMessage } from '../../../utilities/formatSystemMessage';

const AnimatePresenceFixedType = AnimatePresence as ElementType;

interface MessageItemProps {
    message: Message & { isNew?: boolean };
    toggledReactionMenuId: number | null;
    onToggleReactionMenu: (messageId: number) => void;
    onAddReaction: (messageId: number, reactionType: string) => void;
    isDateVisible: boolean;
    onToggleDate: (messageId: number) => void;
    externalUserDetails?: ValidateOtpResponseDto | null
}

const reactionOptions = ["👍", "❤️", "😂", "😮", "😢", "😠"];

const MessageItem: React.FC<MessageItemProps> = ({
    message,
    toggledReactionMenuId,
    onToggleReactionMenu,
    onAddReaction,
    isDateVisible,
    onToggleDate,
    externalUserDetails
}) => {
    console.log("🚀 ~ MessageItem ~ message:", message)
    const { currentUser } = useAuth();
    const UserId_search = currentUser ? currentUser.uid : externalUserDetails?.externalUserDetailId;
    const isMine = message.author.id === UserId_search;
    const reactionCounts = (message.reactions || []).reduce((acc, reaction) => {
        acc[reaction.reactionType] = (acc[reaction.reactionType] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const formatMessageTime = (date?: Date | string) => {
        if (!date) return "";

        return new Date(date).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <div
            className={`chat_txt_flx_reaction message-item-wrapper ${isMine ? 'justify-end' : ''}`}
        >
            {/* If the message type is system type  */}
            {!message.author || message.messageType == MessageType.SYSTEM
                ?
                <div className='system_messages'><small>{formatSystemMessage(message.messageContent)}</small></div>
                : <div className={`chat_txt_flx ${isMine ? 'chat_txt_right' : ''}`}>
                    {!isMine && (
                        <div className={` ${message.isNew ? 'animate-new-message' : ''}`}>
                            <AvatarImage size={"sm"} userId={message.author.id} />
                        </div>
                    )}
                    <div className="flex flex-col" style={isMine ? { alignItems: 'flex-end' } : { alignItems: 'flex-start' }}>
                        {!isMine && (
                            <h3 className="text-[#99A1AF] text-sm mb-1" style={{ fontWeight: "400" }}>
                                {`${message.author.firstName} ${message.author.lastName}`}
                            </h3>
                        )}
                        {message.attachmentUrl && <AttachmentDisplay attachmentUrl={message.attachmentUrl} attachmentType={message.attachmentType} message={message} />}

                        {message.messageContent &&
                            <div
                                key={message.id}
                                // Make the message bubble clickable
                                className={`chat_txt_flx_cntnt relative cursor-pointer ${message.isNew ? 'animate-new-message' : ''}`}
                                onClick={() => onToggleDate(message.id)}
                            >
                                <p
                                    className={`user_message ${isMine ? "text-white" : "text-[#101828]"}`}
                                    style={{ wordBreak: 'break-word' }}>{parseMessageContent(message.messageContent)}
                                </p>

                                {Object.keys(reactionCounts).length > 0 && (
                                    <div className={`${isMine ? 'left-2' : 'right-2'} absolute -bottom-3 flex gap-1`}>
                                        {Object.entries(reactionCounts).map(([reaction, count]) => (
                                            <span key={count} className="px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700 shadow-sm">
                                                {reaction} {count > 1 && count}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        }
                        {/* Time (10:35 AM) */}
                        <span
                            className={`mt-1 text-[11px] text-[#99A1AF] ${isMine ? "self-end mr-1" : "self-start ml-1"}`}
                        >
                            {formatMessageTime(message.createDate)}
                        </span>
                    </div>

                    <div className={`reaction-menu-trigger self-center w-8 h-8 ${!isMine && "hover:bg-gray-100 cursor-pointer"} rounded-full flex items-center justify-center`}
                        onClick={(e) => { e.stopPropagation(); onToggleReactionMenu(message.id); }}
                    >
                        {!isMine && (
                            <FontAwesomeIcon icon={faEllipsis} />
                        )}
                    </div>
                </div>
            }


            {/* Conditionally render the date with an animation */}
            <AnimatePresenceFixedType>
                {isDateVisible && (
                    <motion.div
                        key={"message-date"}
                        className={`w-full text-xs text-gray-500 dark:text-gray-400 mt-1 ${isMine ? 'text-right pr-12' : 'text-left pl-12'}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        {getTimeAgo(message.createDate)}
                    </motion.div>
                )}
            </AnimatePresenceFixedType>

            {toggledReactionMenuId === message.id && !isMine && (
                <div className={`reaction-picker flex gap-2 bg-white dark:bg-gray-800 shadow-lg w-min px-2 rounded-full p-1 mt-1 ml-12`}>
                    {reactionOptions.map((reaction, index) => (
                        <button
                            key={index}
                            onClick={(e) => { e.stopPropagation(); onAddReaction(message.id, reaction); }}
                            className="text-xl hover:scale-125 transition-transform"
                        >
                            {reaction}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MessageItem;